"use client"

import React, { useEffect, useState, useMemo, useRef } from "react"
import {
  ReactFlow,
  useNodesState,
  useEdgesState,
  Background,
  BackgroundVariant,
  Controls,
  Node,
  Edge,
  ReactFlowProvider,
  useReactFlow,
  NodeTypes,
  Handle,
  Position,
} from "@xyflow/react"
import "@xyflow/react/dist/style.css"
import { Loader2, File, Paperclip, FolderOpen, Plus, ChevronDown, ChevronRight, MonitorX } from "lucide-react"
import { useDemoStore } from "@/store/demoStore"
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
  TooltipProvider,
} from "@/components/ui/tooltip"
import { demoApi } from "@/services/demoApi"
import { useTreeConversion } from "@/hooks/useTreeConversion"
import { applyGridLayout } from "@/utils/gridLayout"
import { useDebouncedCallback } from "use-debounce"

// Helper function to get parent folder name
const getParentFolderName = (filePath: string): string => {
  if (!filePath) return ""
  const norm = filePath.replace(/\\/g, "/").replace(/\/+/g, "/")
  const cleaned = norm.length > 1 ? norm.replace(/\/$/, "") : norm
  const parts = cleaned.split("/")
  if (parts.length <= 1) return ""
  parts.pop() // remove filename
  const parent = parts.pop() || ""
  return parent
}

// File Node for Demo (matches frontend structure)
const DemoFileNode: React.FC<{ data: any; selected: boolean; id: string }> = ({ data, selected, id }) => {
  const selectedPaths = useDemoStore((state) => state.selectedPaths)
  const addPath = useDemoStore((state) => state.addPath)
  const removePath = useDemoStore((state) => state.removePath)
  const isSelected = selectedPaths.includes(data.filePath)

  const truncateMiddle = (text: string, maxLength: number): string => {
    if (text.length <= maxLength) return text
    const ellipsis = "..."
    const charsToShow = maxLength - ellipsis.length
    const frontChars = Math.ceil(charsToShow / 2)
    const backChars = Math.floor(charsToShow / 2)
    return text.slice(0, frontChars) + ellipsis + text.slice(-backChars)
  }

  const parentLabel = useMemo(
    () => getParentFolderName(data.filePath) || "(root)",
    [data.filePath]
  )

  const handleClick = () => {
    if (isSelected) {
      removePath(data.filePath)
    } else {
      addPath(data.filePath)
    }
  }

  return (
    <div
      onClick={handleClick}
      className={`relative bg-white border-2 rounded-lg w-[160px] cursor-pointer transition-all ${
        isSelected
          ? "border-blue-500"
          : "border-gray-200 hover:border-gray-300"
      }`}
    >
      {isSelected && (
        <div className="absolute -top-2 -right-2 bg-[#3BCF8E] text-white rounded-full w-5 h-5 flex items-center justify-center text-xs font-bold z-10">
          ✓
        </div>
      )}

      {/* Header */}
      <div className="flex items-center justify-between p-2 bg-gray-50 border-b border-gray-200 gap-2 rounded-t-[6px] overflow-hidden h-[36px]">
        <div className="flex items-center gap-1 flex-1 min-w-0">
          <File size={14} className="text-blue-600 flex-shrink-0" />
          <Tooltip>
            <TooltipTrigger asChild>
              <span className="text-xs font-medium text-gray-700">
                {truncateMiddle(data.label, 12)}
              </span>
            </TooltipTrigger>
            <TooltipContent side="top">
              <p>{data.label}</p>
            </TooltipContent>
          </Tooltip>
        </div>
        {/* Attach button */}
        {data.onAttach && (
          <Tooltip>
            <TooltipTrigger asChild>
              <button
                onClick={(e) => {
                  e.stopPropagation()
                  data.onAttach?.()
                }}
                className="h-5 w-5 flex items-center justify-center rounded transition-colors flex-shrink-0 text-gray-600 hover:text-gray-700 hover:bg-gray-100"
              >
                <Paperclip size={12} />
              </button>
            </TooltipTrigger>
            <TooltipContent side="top">
              <p>Attach to chat</p>
            </TooltipContent>
          </Tooltip>
        )}
      </div>

      {/* Parent folder only */}
      <div className="p-2 text-xs text-gray-500 overflow-hidden h-[36px] flex items-center">
        <Tooltip>
          <TooltipTrigger asChild>
            <div className="truncate">
              {parentLabel === "(root)" ? (
                "(root)"
              ) : (
                <>
                  <span className="text-gray-500">/</span>
                  {parentLabel}
                </>
              )}
            </div>
          </TooltipTrigger>
          <TooltipContent side="bottom">
            <p>/{data.filePath}</p>
          </TooltipContent>
        </Tooltip>
      </div>
      {/* Handles - centered on border */}
      {data.hasIncomingConnection && (
        <Handle
          type="target"
          position={Position.Left}
          isConnectable={false}
          style={{
            width: '6px',
            height: '6px',
            background: '#555',
            border: 'none',
            left: '-4px',
            top: '50%',
            transform: 'translateY(-50%)',
          }}
        />
      )}
      {data.hasOutgoingConnection && (
        <Handle
          type="source"
          position={Position.Right}
          isConnectable={false}
          style={{
            width: '6px',
            height: '6px',
            background: '#555',
            border: 'none',
            right: '-4px',
            left: 'auto',
            top: '50%',
            transform: 'translateY(-50%)',
          }}
        />
      )}
    </div>
  )
}

// Directory Node for Demo (matches frontend structure)
const DemoDirectoryNode: React.FC<{ data: any; selected: boolean; id: string }> = ({ data, selected, id }) => {
  const expandDirectory = useDemoStore((state) => state.expandDirectory)
  const toggleDirectoryCollapse = useDemoStore((state) => state.toggleDirectoryCollapse)
  const isCollapsed = useDemoStore((state) => state.collapsedDirectories?.[id])

  const parentLabel = useMemo(
    () => getParentFolderName(data.filePath) || "(root)",
    [data.filePath]
  )

  const handleExpandClick = (e: React.MouseEvent) => {
    e.stopPropagation()
    expandDirectory(id)
  }

  const handleToggleCollapse = (e: React.MouseEvent) => {
    e.stopPropagation()
    toggleDirectoryCollapse(id)
  }

  return (
    <div
      className={`bg-white border-2 rounded-lg w-[160px] relative ${selected ? "border-blue-500" : "border-gray-200 hover:border-gray-300"}`}
    >
      <div className="flex items-center justify-between p-2 bg-blue-50 border-b border-gray-200 gap-2 rounded-t-[6px] overflow-hidden h-[36px]">
        <div className="flex items-center gap-1 flex-1 min-w-0">
          <FolderOpen size={14} className="text-blue-600 flex-shrink-0" />
          <Tooltip>
            <TooltipTrigger asChild>
              <span className="text-xs font-semibold text-gray-700 truncate max-w-[90px]">
                {data.label}
              </span>
            </TooltipTrigger>
            <TooltipContent side="top">
              <p>{data.label}</p>
            </TooltipContent>
          </Tooltip>
        </div>
        <div className="flex items-center">
          {!isCollapsed && data.hasMoreChildren && (
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  onClick={handleExpandClick}
                  className="h-6 w-6 flex items-center justify-center hover:bg-blue-100 rounded transition-colors flex-shrink-0"
                >
                  <Plus size={14} className="text-blue-600" />
                </button>
              </TooltipTrigger>
              <TooltipContent side="top">
                <p>Show 5 more ({data.totalChildren - data.visibleChildren} remaining)</p>
              </TooltipContent>
            </Tooltip>
          )}
          {data.totalChildren > 0 && (
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  onClick={handleToggleCollapse}
                  className="h-6 w-6 flex items-center justify-center hover:bg-blue-100 rounded transition-colors flex-shrink-0"
                >
                  {isCollapsed ? (
                    <ChevronRight size={14} className="text-blue-600" />
                  ) : (
                    <ChevronDown size={14} className="text-blue-600" />
                  )}
                </button>
              </TooltipTrigger>
              <TooltipContent side="top">
                <p>{isCollapsed ? "Expand children" : "Collapse children"}</p>
              </TooltipContent>
            </Tooltip>
          )}
        </div>
      </div>

      <div className="p-2 overflow-hidden h-[36px] flex items-center">
        <Tooltip>
          <TooltipTrigger asChild>
            <div className="text-xs text-gray-500 truncate">
              {parentLabel === "(root)" ? (
                "(root)"
              ) : (
                <>
                  <span className="text-gray-500">/</span>
                  {parentLabel}
                </>
              )}
            </div>
          </TooltipTrigger>
          <TooltipContent side="bottom">
            <p>/{data.filePath}</p>
          </TooltipContent>
        </Tooltip>
      </div>
      {/* Handles - centered on border */}
      {data.hasIncomingConnection && (
        <Handle
          type="target"
          position={Position.Left}
          isConnectable={false}
          style={{
            width: '6px',
            height: '6px',
            background: '#555',
            border: 'none',
            left: '-4px',
            top: '50%',
            transform: 'translateY(-50%)',
          }}
        />
      )}
      {data.hasOutgoingConnection && (
        <Handle
          type="source"
          position={Position.Right}
          isConnectable={false}
          style={{
            width: '6px',
            height: '6px',
            background: '#555',
            border: 'none',
            right: '-4px',
            left: 'auto',
            top: '50%',
            transform: 'translateY(-50%)',
          }}
        />
      )}
    </div>
  )
}

const nodeTypes: NodeTypes = {
  fileNode: DemoFileNode,
  directoryNode: DemoDirectoryNode,
}

// Ensure unique edge IDs (same as frontend)
function ensureUniqueEdgeIds(edges: Edge[]): Edge[] {
  const seen = new Set<string>()
  return edges.map((e) => {
    let id = e.id
    let counter = 0
    while (seen.has(id)) {
      counter++
      id = `${e.id}-${counter}`
    }
    seen.add(id)
    return { ...e, id }
  })
}

// Removed FitOnEmpty - it was causing zoom to be overridden
// We rely on SetInitialZoom and onInit to set the initial zoom

const FocusOnLibNode: React.FC<{ nodes: Node[]; hasFocusedOnLib: React.MutableRefObject<boolean> }> = ({ nodes, hasFocusedOnLib }) => {
  const rf = useReactFlow()

  useEffect(() => {
    // Only focus once after nodes are loaded
    if (nodes.length > 0 && !hasFocusedOnLib.current) {
      // Find the "lib" node
      const libNode = nodes.find((node) => node.data?.label === "lib")
      
      if (libNode) {
        try {
          // Animate to the lib node with the same pattern as the app's search feature
          rf.setCenter(libNode.position.x + 450, libNode.position.y + 500, { zoom: 0.6, duration: 400 })
          hasFocusedOnLib.current = true
        } catch (error) {
          // Silently handle errors
        }
      }
    }
  }, [nodes, rf, hasFocusedOnLib])

  return null
}

const SetInitialZoom: React.FC<{ nodes: Node[]; hasSetZoom: React.MutableRefObject<boolean>; hasLoadedNodesOnce: React.MutableRefObject<boolean> }> = ({ nodes, hasSetZoom, hasLoadedNodesOnce }) => {
  const rf = useReactFlow()

  useEffect(() => {
    // Only set zoom once when nodes are first loaded (not empty)
    if (nodes.length > 0 && !hasSetZoom.current) {
      try {
        rf.setViewport({ x: 100, y: 250, zoom: 0.6 })
        hasSetZoom.current = true
        hasLoadedNodesOnce.current = true // Mark that nodes have been loaded at least once
      } catch (error) {
        // Silently handle errors
      }
    } else if (nodes.length > 0) {
      // Mark that nodes have been loaded (even if we've already set zoom)
      hasLoadedNodesOnce.current = true
    }
  }, [nodes.length, rf, hasSetZoom, hasLoadedNodesOnce])

  return null
}

const DemoExplorerInner: React.FC = () => {
  const [nodes, setNodes, onNodesChange] = useNodesState<Node>([])
  const [edges, setEdges, onEdgesChange] = useEdgesState<Edge>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [treeData, setTreeData] = useState<any>(null)
  const hasSetInitialZoom = useRef(false)
  const hasLoadedNodesOnce = useRef(false)
  const hasFocusedOnLib = useRef(false)
  const [hoveredNodeId, setHoveredNodeId] = useState<string | null>(null)
  const addPath = useDemoStore((state) => state.addPath)

  const { convertTreeToFlow } = useTreeConversion()
  const wrapperRef = useRef<HTMLDivElement | null>(null)

  // Get expanded/collapsed directories from store
  const expandedDirectories = useDemoStore((state) => state.expandedDirectories)
  const collapsedDirectories = useDemoStore((state) => state.collapsedDirectories)

  // Fetch tree data
  useEffect(() => {
    const fetchTree = async () => {
      try {
        setLoading(true)
        setError(null)

        const data = await demoApi.fetchTree()
        setTreeData(data)
      } catch (err) {
        // Only log in development to avoid console errors in Lighthouse
        if (process.env.NODE_ENV === 'development') {
          console.error("Error fetching tree:", err)
        }
        setError(err instanceof Error ? err.message : "Failed to load repository tree. Please try again later.")
        setTreeData(null)
      } finally {
        setLoading(false)
      }
    }

    fetchTree()
  }, [])

  // Memoize the calculation of nodes and edges to prevent recalculation on unrelated state changes
  const { flowNodes, flowEdges } = useMemo(() => {
    if (!treeData || !treeData.tree) {
      return { flowNodes: [], flowEdges: [] }
    }

    const { nodes: rawNodes, edges: rawEdges } = convertTreeToFlow(
      treeData,
      expandedDirectories,
      collapsedDirectories
    )
    
    // Ensure unique edge IDs (same as frontend)
    const uniqueEdges = ensureUniqueEdgeIds(rawEdges)

    // Add onAttach callback to file nodes
    const nodesWithAttach = rawNodes.map((node) => {
      if (node.type === "fileNode" && node.data?.filePath) {
        return {
          ...node,
          data: {
            ...node.data,
            onAttach: () => {
              addPath(node.data.filePath)
            },
          },
        }
      }
      return node
    })

    // Use grid layout for horizontal rows of 5 siblings (same as frontend)
    const { nodes: laidNodes, edges: laidEdges } = applyGridLayout(
      nodesWithAttach,
      uniqueEdges,
      {
        nodeWidth: 240,
        nodeHeight: 72,
        horizontalSpacing: 60,
        verticalSpacing: 120,
        rowSpacing: 80,
      }
    )

    return { flowNodes: laidNodes, flowEdges: laidEdges }
  }, [treeData, expandedDirectories, collapsedDirectories, convertTreeToFlow, addPath])

  // Apply glow effect to edges when their target node is hovered
  const edgesWithGlow = useMemo(() => {
    if (!hoveredNodeId) return edges
    
    return edges.map((edge) => {
      if (edge.target === hoveredNodeId) {
        const edgeColor = edge.style?.stroke || '#555'
        // Create glow effect using filter with drop-shadow
        return {
          ...edge,
          style: {
            ...edge.style,
            strokeWidth: (edge.style?.strokeWidth as number || 2.5) + 1,
            filter: `drop-shadow(0 0 6px ${edgeColor}) drop-shadow(0 0 10px ${edgeColor})`,
          },
        }
      }
      return edge
    })
  }, [edges, hoveredNodeId])

  // Debounce setting nodes/edges to batch rapid updates
  const debouncedSetNodes = useDebouncedCallback(
    (nodes: Node[]) => {
      setNodes(nodes)
    },
    150 // 150ms debounce delay for smooth user experience
  )

  const debouncedSetEdges = useDebouncedCallback(
    (edges: Edge[]) => {
      setEdges(edges)
    },
    150
  )

  // Set nodes and edges when memoized values change
  useEffect(() => {
    debouncedSetNodes(flowNodes)
    debouncedSetEdges(flowEdges)
  }, [flowNodes, flowEdges, debouncedSetNodes, debouncedSetEdges])

  if (loading) {
    return (
      <div className="w-full h-full flex items-center justify-center">
        <div className="flex flex-col items-center gap-2">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
          <p className="text-sm text-muted-foreground">Loading repository tree...</p>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="w-full h-full flex items-center justify-center p-4">
        <div className="p-4 rounded-md bg-destructive/10 border border-destructive/20 text-destructive text-sm max-w-md text-center">
          <MonitorX className="h-8 w-8 mx-auto mb-2" />
          <p className="font-medium">{error}</p>
        </div>
      </div>
    )
  }

  return (
    <TooltipProvider>
      <div className="w-full h-full" style={{ width: "100%", height: "100%" }} ref={wrapperRef}>
        <SetInitialZoom nodes={nodes} hasSetZoom={hasSetInitialZoom} hasLoadedNodesOnce={hasLoadedNodesOnce} />
        <FocusOnLibNode nodes={nodes} hasFocusedOnLib={hasFocusedOnLib} />
        <ReactFlow
          nodes={nodes}
          edges={edgesWithGlow}
          onNodesChange={onNodesChange}
          onEdgesChange={onEdgesChange}
          onNodeMouseEnter={(event, node) => setHoveredNodeId(node.id)}
          onNodeMouseLeave={() => setHoveredNodeId(null)}
          nodeTypes={nodeTypes}
          minZoom={0.1}
          onInit={(instance) => {
            instance.setViewport({ x: 100, y: 250, zoom: 0.6 })
          }}
          maxZoom={2}
          nodesDraggable={true}
          nodesConnectable={false}
          elementsSelectable={true}
          defaultEdgeOptions={{
            animated: false,
            type: "smoothstep",
          }}
          proOptions={{ hideAttribution: true }}
        >
          <Background variant={BackgroundVariant.Dots} gap={20} size={1} />
          <Controls />
        </ReactFlow>
      </div>
    </TooltipProvider>
  )
}

export const DemoExplorer: React.FC = () => {
  return (
    <ReactFlowProvider>
      <DemoExplorerInner />
    </ReactFlowProvider>
  )
}
